import { AxiosRequestConfig } from 'axios';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useAxiosWithAuth } from './useAxios';
import { useStableArray } from './useStableArray';

/**
 *
 * @param config the axios config to override
 * @param _filterKeys the keys which are used as URL params to filter by the current query filter string.
 * @returns
 */
export const usePaginatedAxios = <T extends object>(
  config: AxiosRequestConfig<any>,
  /**
   * @private Is not stable, so only use the stabilized version of this array
   */
  _filterKeys: string[]
) => {
  const pageRef = useRef(0);
  const filterValueRef = useRef('');

  // Prevents unnecessary re-renders and the need of memoizing the array on every usage
  const stableFilterKeys = useStableArray(_filterKeys);

  const [options, setOptions] = useState<T[]>();

  const [{ data, loading }, fetch] = useAxiosWithAuth<{
    content: T[];
    page: { total_elements: number; number: number; total_pages: number };
  }>(
    { ...config },
    {
      manual: true,
      autoCancel: true
    }
  );

  const fetchWithParams = useCallback(async () => {
    const filterParams = stableFilterKeys.reduce<{ [key: string]: string }>(
      (acc, key) => {
        acc[key] = filterValueRef.current;
        return acc;
      },
      {}
    );
    return fetch({
      params: {
        ...config.params,
        page: pageRef.current,
        operator: 'OR',
        ...filterParams
      }
    }).catch();
  }, [config.params, fetch, stableFilterKeys]);

  const changeFilter = useCallback(
    (query: string) => {
      filterValueRef.current = query;
      pageRef.current = 0;
      fetchWithParams().then(({ data }) => {
        setOptions([...data.content]);
      });
    },
    [fetchWithParams]
  );

  const fetchNextPage = useCallback(() => {
    pageRef.current += 1;
    fetchWithParams().then(({ data }) => {
      setOptions(prev => [...(prev ?? []), ...data.content]);
    });
  }, [fetchWithParams]);

  const hasMorePages = useMemo(
    () => (data ? pageRef.current >= data.page.total_pages - 1 : false),
    [data]
  );

  // Initial load
  useEffect(() => {
    fetchWithParams().then(({ data }) => {
      setOptions([...data.content]);
    });
  }, [fetchWithParams]);

  return {
    changeFilter,
    fetchNextPage,
    loading,
    reachedLastPage: hasMorePages,
    options
  };
};
